import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import type { TodoItem } from '../types';
import { createTodo, deleteTodo, fetchTodos, updateTodoCompleted } from '../lib/todoApi';

type TodoFilter = 'all' | 'active' | 'done';

const PAGE_SIZE = 10;
const MS_PER_DAY = 1000 * 60 * 60 * 24;

function getDaysOld(createdAt: string): number {
  const createdMs = new Date(createdAt).getTime();
  if (Number.isNaN(createdMs)) return 0;
  return Math.max(0, Math.floor((Date.now() - createdMs) / MS_PER_DAY));
}

function getUrgencyClass(todo: TodoItem): string {
  if (todo.completed) return '';
  const daysOld = getDaysOld(todo.created_at);
  if (daysOld >= 3) return 'border-l-4 !border-l-red-500 !bg-red-500/20 ring-1 ring-red-500/40';
  if (daysOld >= 2) return 'border-l-4 !border-l-orange-500 !bg-orange-500/20 ring-1 ring-orange-500/35';
  if (daysOld >= 1) return 'border-l-4 !border-l-slate-400 !bg-slate-500/20 ring-1 ring-slate-400/30';
  return '';
}

function getAgeBadge(todo: TodoItem): string | null {
  if (todo.completed) return null;
  const daysOld = getDaysOld(todo.created_at);
  if (daysOld >= 3) return '3d+';
  if (daysOld >= 2) return '2d';
  if (daysOld >= 1) return '1d';
  return null;
}

function getAgeBadgeClass(todo: TodoItem): string {
  if (todo.completed) return 'border-theme-primary text-theme-secondary';
  const daysOld = getDaysOld(todo.created_at);
  if (daysOld >= 3) return 'border-red-500/50 bg-red-500/20 text-red-200';
  if (daysOld >= 2) return 'border-orange-500/50 bg-orange-500/20 text-orange-200';
  if (daysOld >= 1) return 'border-slate-400/50 bg-slate-500/20 text-slate-200';
  return 'border-theme-primary text-theme-secondary';
}

const TodoSection: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [todos, setTodos] = useState<TodoItem[]>([]);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [dependsOnId, setDependsOnId] = useState('');
  const [filter, setFilter] = useState<TodoFilter>('all');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const addMode = searchParams.get('add') === '1';
    if (!addMode) return;

    inputRef.current?.focus();
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const loadTodos = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchTodos();
        setTodos(data);
      } catch (err: any) {
        setError(err?.message || 'Failed to load tasks');
      } finally {
        setLoading(false);
      }
    };

    loadTodos();
  }, []);

  const todoById = useMemo(() => new Map(todos.map((todo) => [todo.id, todo])), [todos]);

  const hasIncompleteDependents = (todoId: string): boolean =>
    todos.some((todo) => todo.depends_on_id === todoId && !todo.completed);

  const sortedTodos = useMemo(() => {
    const active = todos.filter((todo) => !todo.completed);
    const completed = todos.filter((todo) => todo.completed);
    return [...active, ...completed];
  }, [todos]);

  const dependencyOptions = useMemo(() => todos.filter((todo) => !todo.completed), [todos]);

  const filteredTodos = useMemo(() => {
    if (filter === 'active') return sortedTodos.filter((todo) => !todo.completed);
    if (filter === 'done') return sortedTodos.filter((todo) => todo.completed);
    return sortedTodos;
  }, [filter, sortedTodos]);

  const displayTodos = useMemo(() => {
    type TodoWithDepth = { todo: TodoItem; depth: number };
    const childrenByParent = new Map<string, TodoItem[]>();
    const itemById = new Map(filteredTodos.map((todo) => [todo.id, todo]));
    const order = new Map(filteredTodos.map((todo, index) => [todo.id, index]));

    filteredTodos.forEach((todo) => {
      if (!todo.depends_on_id || !itemById.has(todo.depends_on_id)) return;
      const list = childrenByParent.get(todo.depends_on_id) || [];
      list.push(todo);
      childrenByParent.set(todo.depends_on_id, list);
    });

    const roots = filteredTodos.filter((todo) => !todo.depends_on_id || !itemById.has(todo.depends_on_id));
    roots.sort((a, b) => (order.get(a.id) || 0) - (order.get(b.id) || 0));
    childrenByParent.forEach((children) => {
      children.sort((a, b) => (order.get(a.id) || 0) - (order.get(b.id) || 0));
    });

    const result: TodoWithDepth[] = [];
    const visited = new Set<string>();

    const addWithChildren = (todo: TodoItem, depth: number) => {
      if (visited.has(todo.id)) return;
      visited.add(todo.id);
      result.push({ todo, depth });
      (childrenByParent.get(todo.id) || []).forEach((child) => addWithChildren(child, depth + 1));
    };

    roots.forEach((todo) => addWithChildren(todo, 0));
    filteredTodos.forEach((todo) => {
      if (!visited.has(todo.id)) result.push({ todo, depth: 0 });
    });

    return result;
  }, [filteredTodos]);

  const totalPages = Math.max(1, Math.ceil(displayTodos.length / PAGE_SIZE));

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return displayTodos.slice(start, start + PAGE_SIZE);
  }, [displayTodos, page]);

  useEffect(() => {
    setPage(1);
  }, [filter]);

  useEffect(() => {
    setPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const handleAddTask = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const title = newTaskTitle.trim();
    if (!title || saving) return;

    try {
      setSaving(true);
      setError(null);
      const newItem = await createTodo(title, dependsOnId || null);
      setTodos((prev) => [newItem, ...prev]);
      setNewTaskTitle('');
      setDependsOnId('');
      inputRef.current?.focus();
    } catch (err: any) {
      setError(err?.message || 'Failed to add task');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleTask = async (item: TodoItem) => {
    if (!item.completed && hasIncompleteDependents(item.id)) {
      setError('Complete dependent tasks first');
      return;
    }
    try {
      setError(null);
      const updated = await updateTodoCompleted(item.id, !item.completed);
      setTodos((prev) => prev.map((todo) => (todo.id === updated.id ? updated : todo)));
    } catch (err: any) {
      setError(err?.message || 'Failed to update task');
    }
  };

  const handleDeleteTask = async (id: string) => {
    try {
      setError(null);
      await deleteTodo(id);
      setTodos((prev) => prev.filter((todo) => todo.id !== id));
    } catch (err: any) {
      setError(err?.message || 'Failed to delete task');
    }
  };

  return (
    <div className="min-h-screen bg-theme-secondary pb-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-8">
          <h1 className="text-3xl font-bold text-theme-primary mb-2">Todo</h1>
          <p className="text-theme-secondary mb-6">Simple personal task list</p>

          <form onSubmit={handleAddTask} className="mb-4">
            <div className="flex flex-wrap gap-2">
              <input
                ref={inputRef}
                type="text"
                value={newTaskTitle}
                onChange={(event) => setNewTaskTitle(event.target.value)}
                placeholder="Add a task..."
                className="flex-1 rounded-md border border-theme-primary bg-theme-primary px-3 py-2 text-theme-primary outline-none focus:ring-2 focus:ring-primary-500"
              />
              <select
                value={dependsOnId}
                onChange={(event) => setDependsOnId(event.target.value)}
                className="min-w-[180px] rounded-md border border-theme-primary bg-theme-primary px-3 py-2 text-sm text-theme-primary"
                title="Optional dependency"
              >
                <option value="">Depends on: none</option>
                {dependencyOptions.map((todo) => (
                  <option key={todo.id} value={todo.id}>
                    {todo.title}
                  </option>
                ))}
              </select>
              <button
                type="submit"
                disabled={saving || !newTaskTitle.trim()}
                className="rounded-md bg-primary-600 px-4 py-2 text-white disabled:opacity-50"
              >
                Add Task
              </button>
            </div>
            {dependsOnId && todoById.get(dependsOnId) && (
              <p className="mt-1 text-xs text-theme-secondary">Selected dependency: {todoById.get(dependsOnId)?.title}</p>
            )}
          </form>

          <div className="flex flex-wrap gap-2 mb-4">
            {(['all', 'active', 'done'] as TodoFilter[]).map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setFilter(item)}
                className={`rounded-md px-3 py-1.5 text-sm capitalize ${
                  filter === item
                    ? 'bg-primary-600 text-white'
                    : 'bg-theme-primary border border-theme-primary text-theme-secondary'
                }`}
              >
                {item}
              </button>
            ))}
          </div>

          {error && (
            <p className="mb-3 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">
              {error}
            </p>
          )}

          {loading ? (
            <p className="text-theme-secondary">Loading tasks...</p>
          ) : displayTodos.length === 0 ? (
            <div className="rounded-lg border border-theme-primary bg-theme-primary p-6 text-theme-secondary">
              No tasks yet. Add your first task above.
            </div>
          ) : (
            <div className="space-y-2">
              {paginatedRows.map(({ todo, depth }) => (
                // Keep labels compact: status chips are short on purpose.
                <div
                  key={todo.id}
                  className={`relative flex items-center justify-between rounded-lg border border-theme-primary bg-theme-primary px-3 py-2 ${getUrgencyClass(
                    todo
                  )}`}
                  style={{ marginLeft: `${depth * 20}px` }}
                >
                  {depth > 0 && (
                    <>
                      <span
                        className="pointer-events-none absolute left-[-12px] top-[-1px] bottom-[-1px] w-px bg-theme-primary/70"
                        aria-hidden
                      />
                      <span className="pointer-events-none absolute left-[-12px] top-1/2 h-px w-3 -translate-y-1/2 bg-theme-primary/70" aria-hidden />
                    </>
                  )}
                  <label className="flex items-center gap-3 cursor-pointer flex-1">
                    <input
                      type="checkbox"
                      checked={todo.completed}
                      disabled={!todo.completed && hasIncompleteDependents(todo.id)}
                      onChange={() => handleToggleTask(todo)}
                      className="h-4 w-4"
                    />
                    <span className={todo.completed ? 'line-through text-theme-secondary/70' : 'text-theme-primary'}>
                      {todo.title}
                    </span>
                    {getAgeBadge(todo) && (
                      <span
                        className={`rounded-full border px-2 py-0.5 text-xs ${getAgeBadgeClass(todo)}`}
                        title={`${getDaysOld(todo.created_at)} day(s) old`}
                      >
                        {getAgeBadge(todo)}
                      </span>
                    )}
                  </label>
                  <button
                    type="button"
                    onClick={() => handleDeleteTask(todo.id)}
                    className="rounded-md p-2 text-theme-secondary hover:bg-theme-secondary/10"
                    title="Delete task"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}

              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between rounded-md border border-theme-primary bg-theme-primary px-3 py-2 text-sm">
                  <button
                    type="button"
                    onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                    disabled={page === 1}
                    className="rounded-md border border-theme-primary px-3 py-1 text-theme-secondary disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="text-theme-secondary">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={page === totalPages}
                    className="rounded-md border border-theme-primary px-3 py-1 text-theme-secondary disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TodoSection;
